class CollectionGridSwitcher extends HTMLElement {
  static storageKeys = {
    desktop: 'collection-grid-columns-desktop',
    mobile: 'collection-grid-columns-mobile',
  };

  static allowedColumns = {
    desktop: ['2', '3', '4'],
    mobile: ['1', '2'],
  };

  static mobileMediaQuery = window.matchMedia('(max-width: 749px)');

  constructor() {
    super();
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onGridRendered = this.onGridRendered.bind(this);
    this.onColumnsChange = this.onColumnsChange.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
  }

  connectedCallback() {
    CollectionGridSwitcher.migrateLegacyStorage();
    CollectionGridSwitcher.initResizeListener();

    this.variant = this.dataset.variant || 'mobile';
    this.buttons = this.querySelectorAll('[data-columns]');
    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick));
    document.addEventListener('facets:grid-rendered', this.onGridRendered);
    document.addEventListener('collection-grid:columns-change', this.onColumnsChange);

    const columns = CollectionGridSwitcher.getSavedColumns(this.variant, this.dataset.defaultColumns);
    this.syncButtons(columns);
    CollectionGridSwitcher.applyColumnsToGrid();
  }

  disconnectedCallback() {
    document.removeEventListener('facets:grid-rendered', this.onGridRendered);
    document.removeEventListener('collection-grid:columns-change', this.onColumnsChange);
  }

  onButtonClick(event) {
    CollectionGridSwitcher.setColumns(this.variant, event.currentTarget.dataset.columns);
  }

  onGridRendered() {
    CollectionGridSwitcher.applyColumnsToGrid();
    CollectionGridSwitcher.syncAllButtons();
  }

  onColumnsChange(event) {
    if (event.detail.variant === this.variant) {
      this.syncButtons(event.detail.columns);
    }
  }

  onViewportChange() {
    CollectionGridSwitcher.applyColumnsToGrid();
  }

  syncButtons(columns) {
    this.buttons.forEach((button) => {
      const isActive = button.dataset.columns === String(columns);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive);
    });
  }

  static migrateLegacyStorage() {
    const legacy = localStorage.getItem('collection-grid-columns');
    if (!legacy) return;

    if (!localStorage.getItem(CollectionGridSwitcher.storageKeys.desktop)) {
      localStorage.setItem(CollectionGridSwitcher.storageKeys.desktop, legacy);
    }

    localStorage.removeItem('collection-grid-columns');
  }

  static initResizeListener() {
    if (CollectionGridSwitcher.resizeListenerInitialized) return;

    const onResize = () => {
      window.requestAnimationFrame(() => CollectionGridSwitcher.applyColumnsToGrid());
    };

    CollectionGridSwitcher.mobileMediaQuery.addEventListener('change', onResize);
    window.addEventListener('resize', onResize);
    CollectionGridSwitcher.resizeListenerInitialized = true;
  }

  static getSavedColumns(variant, defaultColumns) {
    const normalizedDefault = CollectionGridSwitcher.normalizeColumns(variant, defaultColumns);
    const storageKey = CollectionGridSwitcher.storageKeys[variant];
    const allowed = CollectionGridSwitcher.allowedColumns[variant];
    const savedColumns = localStorage.getItem(storageKey);
    return allowed.includes(savedColumns) ? savedColumns : normalizedDefault;
  }

  static normalizeColumns(variant, columns) {
    const value = parseInt(columns, 10);

    if (variant === 'desktop') {
      if (value <= 2) return '2';
      if (value >= 4) return '4';
      return '3';
    }

    if (value <= 1) return '1';
    return '2';
  }

  static setColumns(variant, columns, save = true) {
    const normalizedColumns = CollectionGridSwitcher.normalizeColumns(variant, columns);

    if (save) {
      localStorage.setItem(CollectionGridSwitcher.storageKeys[variant], normalizedColumns);
    }

    CollectionGridSwitcher.syncButtonsForVariant(variant, normalizedColumns);
    CollectionGridSwitcher.applyColumnsToGrid();

    if (save) {
      document.dispatchEvent(
        new CustomEvent('collection-grid:columns-change', {
          detail: { variant, columns: normalizedColumns },
        })
      );
    }
  }

  static syncButtonsForVariant(variant, columns) {
    document
      .querySelectorAll(`collection-grid-switcher[data-variant="${variant}"] [data-columns]`)
      .forEach((button) => {
        const isActive = button.dataset.columns === String(columns);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive);
      });
  }

  static syncAllButtons() {
    document.querySelectorAll('collection-grid-switcher').forEach((switcher) => {
      const variant = switcher.dataset.variant || 'mobile';
      const columns = CollectionGridSwitcher.getSavedColumns(variant, switcher.dataset.defaultColumns);
      CollectionGridSwitcher.syncButtonsForVariant(variant, columns);
    });
  }

  static applyColumnsToGrid() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const desktopSwitcher = document.querySelector('collection-grid-switcher[data-variant="desktop"]');
    const mobileSwitcher = document.querySelector('collection-grid-switcher[data-variant="mobile"]');

    const desktopColumns = CollectionGridSwitcher.getSavedColumns(
      'desktop',
      desktopSwitcher?.dataset.defaultColumns || '3'
    );
    const mobileColumns = CollectionGridSwitcher.getSavedColumns(
      'mobile',
      mobileSwitcher?.dataset.defaultColumns || '2'
    );

    for (let i = 1; i <= 6; i += 1) {
      grid.classList.remove(`grid--${i}-col-desktop`);
    }

    grid.classList.remove('grid--1-col-tablet-down', 'grid--2-col-tablet-down');

    if (CollectionGridSwitcher.mobileMediaQuery.matches) {
      grid.classList.add(`grid--${mobileColumns}-col-tablet-down`);
      return;
    }

    grid.classList.add(`grid--${desktopColumns}-col-desktop`);
  }
}

if (!customElements.get('collection-grid-switcher')) {
  customElements.define('collection-grid-switcher', CollectionGridSwitcher);
}
